import { cn } from '@cobliteam/ui/utils';
import { useState } from 'react';

import { useOnboardingStore, useUpdateOnboarding } from '@cobliteam/onboarding';

import useUser from '@cobliteam/hooks/src/useEntity/useUser';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import StorybookLink from './components/StorybookLink';
import TailwindIndicator from './components/TailwindIndicator';

import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import Flags from './components/Flags';
import LocalStorage from './components/LocalStorage';
import Onboarding from './components/Onboarding';
import Permissions from './components/Permissions';
import ShowIconCheckbox from './components/ShowIconCheckbox';
import UserDataComponent from './components/UserData';

const MENU_ITEMS = [
  { title: 'Meus dados', key: 'userData' },
  { title: 'Flags', key: 'flags' },
  { title: 'Onboarding', key: 'onboarding' },
  { title: 'Permissões', key: 'permissions' },
  { title: 'LocalStorage', key: 'localStorage' },
];

const DISABLED_DEV_TOOLS = false;

function usePermissionsDx() {
  const { developerTools } = useFeatureFlags();
  const [showIcon, setShowIcon] = useState(true);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('userData');

  const isLocalhost = window.location.hostname.includes('localhost');
  const isPreview = window.location.hostname.includes('preview');
  const isDev = isLocalhost || isPreview;

  const { getUser, getSession } = useUser();
  const session = getSession();
  const user = getUser();

  const isCobler = user.role === 'cobler';

  const onboarding = useOnboardingStore((state) => ({
    totalSteps: state.onboardingStepsTotal,
    currentStep: state.onboardingStepsIndex,
    hasOnboarding: state.hasOnboarding,
  }));

  const { createOnboarding, resetOnboarding } = useUpdateOnboarding();

  if (DISABLED_DEV_TOOLS) return null;
  if (!isDev) return null;
  if (!developerTools) return null;
  if (!isCobler) return null;

  return (
    <div className='fixed right-40 bottom-4 z-50'>
      <div className='flex flex-row gap-2'>
        <Dialog>
          {showIcon && (
            <Dialog.Trigger asChild>
              <Button size='sm' color='default' startIcon={<Icon name='settings-2' />}>
                Developer tools
              </Button>
            </Dialog.Trigger>
          )}

          <Dialog.Content size='lg'>
            <Dialog.Header>
              <Dialog.Title>Dev. Exp.</Dialog.Title>
              <Dialog.Close />
            </Dialog.Header>

            <Dialog.Body>
              <div className='flex h-[450px]'>
                <nav className='flex w-40 flex-col gap-2 border-gray-6 border-r px-2'>
                  {MENU_ITEMS.map(({ title, key }) => (
                    <a
                      key={key}
                      onClick={() => setSelectedMenuItem(key)}
                      className={cn('block rounded-lg p-3 font-bold transition-all hover:cursor-pointer hover:bg-gray-light', selectedMenuItem === key ? 'bg-gray-light' : '')}
                    >
                      {title}
                    </a>
                  ))}
                </nav>

                <div className='flex w-full flex-1 flex-col overflow-auto px-4'>
                  {selectedMenuItem === 'userData' && <UserDataComponent session={session} user={user} isCobler={isCobler} />}
                  {selectedMenuItem === 'flags' && <Flags session={session} />}
                  {selectedMenuItem === 'onboarding' && <Onboarding onboarding={onboarding} resetOnboarding={resetOnboarding} createOnboarding={createOnboarding} />}
                  {selectedMenuItem === 'permissions' && <Permissions />}
                  {selectedMenuItem === 'localStorage' && <LocalStorage />}
                </div>
              </div>
            </Dialog.Body>

            <Dialog.Footer className='flex justify-between gap-4'>
              <ShowIconCheckbox showIcon={showIcon} setShowIcon={setShowIcon} />
              <Dialog.Close asChild>
                <Button variant='text'>Fechar</Button>
              </Dialog.Close>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog>

        <TailwindIndicator />
        <StorybookLink />
      </div>
    </div>
  );
}

export default usePermissionsDx;
